import {
  IconClipboardList,
  IconClock,
  IconContract,
  IconFileStack,
  IconInbox,
  IconLandmark,
  IconScales,
} from '@shared/ui/icons';
import {
  BigNumber,
  EmptyHint,
  Grid,
  LivePulse,
  LivePulseTag,
  Metric,
  Metrics,
  SectionLabel,
} from './styles';
import { TileShell, TileSkeleton } from './TileShell';

interface LegalWorkSectionProps {
  isLoading: boolean;
  precontractsTotal: number;
  contractsTotal: number;
  underReview: number;
  analyzed: number;
  counterpartiesTotal: number;
  atRiskCount: number;
  liquidatedCount: number;
  claimsTotal: number;
  activeClaims: number;
  lawsuits: number;
  corrTotal: number;
  unclassified: number;
  classified: number;
  returned: number;
  deadlinesTotal: number;
  overdue: number;
  within7: number;
  nextDl: { due_at: string } | null;
  govTotal: number;
  govOverdue: number;
  govInWork: number;
  lnaTotal: number;
  lnaActive: number;
  lnaExpired: number;
}

export function LegalWorkSection({
  isLoading,
  precontractsTotal,
  contractsTotal,
  underReview,
  analyzed,
  counterpartiesTotal,
  atRiskCount,
  liquidatedCount,
  claimsTotal,
  activeClaims,
  lawsuits,
  corrTotal,
  unclassified,
  classified,
  returned,
  deadlinesTotal,
  overdue,
  within7,
  nextDl,
  govTotal,
  govOverdue,
  govInWork,
  lnaTotal,
  lnaActive,
  lnaExpired,
}: LegalWorkSectionProps) {
  return (
    <>
      <SectionLabel>Юридическая работа</SectionLabel>
      <Grid>
        <TileShell
          to="/agents/statut/precontracts"
          delay={0}
          icon={IconFileStack}
          title="Преддоговорная работа"
          note=" — незаключённые договоры на проработке"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{precontractsTotal}</BigNumber>
              <EmptyHint>Проекты и черновики до заключения — та же экспертиза рисков</EmptyHint>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/contracts"
          delay={30}
          icon={IconContract}
          title="Договоры"
          note=" — экспертиза рисков по 7 категориям"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{contractsTotal}</BigNumber>
              <Metrics>
                <Metric $tone={underReview > 0 ? 'success' : undefined}>
                  {underReview > 0 ? (
                    <LivePulseTag>
                      <LivePulse />
                      Под анализом
                    </LivePulseTag>
                  ) : (
                    'Под анализом'
                  )}
                  <strong>{underReview}</strong>
                </Metric>
                <Metric $tone="success">
                  Проанализировано
                  <strong>{analyzed}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/counterparties"
          delay={60}
          icon={IconLandmark}
          title="Контрагенты"
          note="реестр с проверкой ЕГРЮЛ"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{counterpartiesTotal}</BigNumber>
              <Metrics>
                <Metric $tone={atRiskCount > 0 ? 'warning' : 'default'}>
                  Под риском
                  <strong>{atRiskCount}</strong>
                </Metric>
                <Metric $tone={liquidatedCount > 0 ? 'danger' : 'default'}>
                  Прекратили
                  <strong>{liquidatedCount}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/claims"
          delay={120}
          icon={IconScales}
          title="Претензии и иски"
          note=" — реестр споров, расчёт неустойки"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{claimsTotal}</BigNumber>
              <Metrics>
                <Metric>
                  Активные
                  <strong>{activeClaims}</strong>
                </Metric>
                <Metric>
                  Из них в суде
                  <strong>{lawsuits}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/correspondence"
          delay={120}
          icon={IconInbox}
          title="Корреспонденция"
          note=" — приём от диспетчера, LLM-классификация"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{corrTotal}</BigNumber>
              <Metrics>
                <Metric $tone={unclassified > 0 ? 'success' : 'default'}>
                  {unclassified > 0 ? (
                    <LivePulseTag>
                      <LivePulse />
                      На классификации
                    </LivePulseTag>
                  ) : (
                    'На классификации'
                  )}
                  <strong>{unclassified}</strong>
                </Metric>
                <Metric $tone="success">
                  Классифицировано
                  <strong>{classified}</strong>
                </Metric>
                <Metric>
                  Возвращено
                  <strong>{returned}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          // Есть просрочки — ведём прямо к ним: красная цифра должна открывать
          // то, что она считает, а не активные сроки (просроченных там нет).
          to={overdue > 0 ? '/agents/statut/deadlines?state=overdue' : '/agents/statut/deadlines'}
          delay={180}
          icon={IconClock}
          title="Сроки"
          note="0 пропусков — целевая метрика "
        >
          {isLoading ? (
            <TileSkeleton />
          ) : deadlinesTotal === 0 ? (
            <>
              <BigNumber>—</BigNumber>
              <EmptyHint>Активных сроков на 30 дней нет.</EmptyHint>
            </>
          ) : (
            <>
              <BigNumber>{deadlinesTotal}</BigNumber>
              <Metrics>
                {overdue > 0 && (
                  <Metric $tone="danger">
                    Просрочено
                    <strong>{overdue}</strong>
                  </Metric>
                )}
                <Metric $tone={within7 > 0 ? 'warning' : 'default'}>
                  Ближайшие 7 дней
                  <strong>{within7}</strong>
                </Metric>
                {nextDl && (
                  <Metric>
                    Ближайший
                    <strong>{new Date(nextDl.due_at).toLocaleDateString('ru-RU')}</strong>
                  </Metric>
                )}
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/gov"
          delay={240}
          icon={IconLandmark}
          title="Госорганы"
          note=" — ФНС, прокуратура, надзоры"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : govTotal === 0 ? (
            <>
              <BigNumber>—</BigNumber>
              <EmptyHint>Запросов от госорганов нет.</EmptyHint>
            </>
          ) : (
            <>
              <BigNumber>{govTotal}</BigNumber>
              <Metrics>
                {govOverdue > 0 && (
                  <Metric $tone="danger">
                    Просрочено
                    <strong>{govOverdue}</strong>
                  </Metric>
                )}
                <Metric $tone={govInWork > 0 ? 'warning' : 'default'}>
                  В работе
                  <strong>{govInWork}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/lna"
          delay={300}
          icon={IconClipboardList}
          title="ЛНА"
          note=" — приказы, положения, регламенты"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{lnaTotal}</BigNumber>
              <Metrics>
                <Metric $tone="success">
                  Действуют
                  <strong>{lnaActive}</strong>
                </Metric>
                {lnaExpired > 0 && (
                  <Metric $tone="warning">
                    Истекли
                    <strong>{lnaExpired}</strong>
                  </Metric>
                )}
              </Metrics>
            </>
          )}
        </TileShell>
      </Grid>
    </>
  );
}
